import React, { useRef } from "react";
import PropTypes from "prop-types";

import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";
import {
  DropdownSelect,
  DropdownSelectDefaultTagName,
  DropdownSelectDefaultTag,
  DropdownSelectBaseProps,
  dropdownSelectNativeVariants,
} from "../DropdownSelect";

export const DropdownMultiSelectDefaultTagName = DropdownSelectDefaultTagName;
export type DropdownMultiSelectDefaultTag = DropdownSelectDefaultTag;

export type DropdownMultiSelectBaseProps<
  Tag extends React.ElementType = DropdownSelectDefaultTag
> = Omit<DropdownSelectBaseProps<Tag>, "multiple">;

const defaultProps = {
  tag: DropdownMultiSelectDefaultTagName,
  children: null,
  itemLabels: undefined,
  emptyLabel: "",
  nativeVariant: "byHover",
  customClass: undefined,
  contentCustomClass: undefined,
  listCustomClass: undefined,
  listItemCustomClass: undefined,
  id: undefined,
  value: [] as string[],
  prefix: "",
  isValid: undefined,
  onChange: undefined,
  afterOpen: undefined,
  afterClose: undefined,
  isOpen: undefined,
  minSelectionCount: 0,
  maxSelectionCount: undefined,
  valueRenderer: (prefix: string, value: string[] | undefined) =>
    value && value.length > 0 ? `${prefix} (${value.length})` : "",
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  DropdownMultiSelectBaseProps,
  typeof defaultProps
> = defaultProps;

const propTypes = {
  tag: PropTypes.elementType,
  nativeVariant: PropTypes.oneOf(dropdownSelectNativeVariants),
  emptyLabel: PropTypes.string,
  customClass: PropTypes.string,
  contentCustomClass: PropTypes.string,
  listCustomClass: PropTypes.string,
  listItemCustomClass: PropTypes.string,
  id: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  itemLabels: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.arrayOf(PropTypes.string.isRequired),
  prefix: PropTypes.string,
  isValid: PropTypes.bool,
  onChange: PropTypes.func,
  afterOpen: PropTypes.func,
  afterClose: PropTypes.func,
  isOpen: PropTypes.bool,
  minSelectionCount: PropTypes.number,
  maxSelectionCount: PropTypes.number,
  valueRenderer: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
} as const;

export type DropdownMultiSelectProps<
  Tag extends React.ElementType = DropdownMultiSelectDefaultTag
> = DropdownMultiSelectBaseProps<Tag> &
  Omit<
    React.ComponentPropsWithRef<Tag>,
    keyof DropdownMultiSelectBaseProps<Tag>
  >;

export type DropdownMultiSelectPropsWithoutRef<
  Tag extends React.ElementType = DropdownMultiSelectDefaultTag
> = DropdownMultiSelectBaseProps<Tag> &
  Omit<
    React.ComponentPropsWithoutRef<Tag>,
    keyof DropdownMultiSelectBaseProps<Tag>
  >;

const DropdownMultiSelectWithRef = <
  Tag extends React.ElementType = DropdownMultiSelectDefaultTag
>(
  props: DropdownMultiSelectPropsWithoutRef<Tag>,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
): JSX.Element => {
  const { tag, items, itemLabels, children, ...restProps } = props;

  const selectRef = useRef(null);

  const setElementRef = (
    elementRef: React.MutableRefObject<HTMLElement | null>,
    forwardedRef:
      | React.MutableRefObject<HTMLElement | null>
      | React.RefCallback<HTMLElement>
  ) => (element: HTMLElement | null) => {
    // eslint-disable-next-line no-param-reassign
    elementRef.current = element;
    if (typeof forwardedRef === "function") {
      forwardedRef(element);
    } else if (forwardedRef) {
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = element;
    }
  };

  return (
    <DropdownSelect
      ref={setElementRef(selectRef, ref)}
      multiple
      tag={tag as "div"}
      items={items}
      itemLabels={itemLabels}
      {...restProps}
    >
      {children}
    </DropdownSelect>
  );
};

export const DropdownMultiSelect = (React.forwardRef(
  DropdownMultiSelectWithRef
) as unknown) as (<
  Tag extends React.ElementType = DropdownMultiSelectDefaultTag
>(
  props: DropdownMultiSelectProps<Tag>
) => ReturnType<typeof DropdownMultiSelectWithRef>) &
  ReactComponentStaticProps<DropdownMultiSelectProps, "tag">;

DropdownMultiSelect.displayName = "DropDownMultiSelect";

DropdownMultiSelect.propTypes = propTypes;

DropdownMultiSelect.defaultProps = validatedDefaultProps;

export default DropdownMultiSelect;
